import { GameObject } from '../../../types/game.types';

export class PhysicsSystem {
  private gameWidth: number;
  private gameHeight: number;

  constructor(gameWidth: number, gameHeight: number) {
    this.gameWidth = gameWidth;
    this.gameHeight = gameHeight;
  }

  public updateEntity(entity: GameObject, deltaTime: number): void {
    if (!entity.active) return;

    // Apply wraparound physics for entities that support it
    this.applyWraparound(entity);
  }

  private applyWraparound(entity: GameObject): void {
    // Entities handle their own wraparound logic in their update methods
    // This system could be extended for more complex physics if needed
  }

  public checkWraparoundCollision(entity1: GameObject, entity2: GameObject): boolean {
    // Check collision accounting for wraparound
    // This is useful for entities that might be colliding across screen edges
    
    const e1Bounds = entity1.getBounds();
    const e2Bounds = entity2.getBounds();
    
    // Normal collision check
    if (this.rectanglesCollide(e1Bounds, e2Bounds)) {
      return true;
    }

    // Check wraparound collisions
    const positions = [
      // Check 8 surrounding "virtual" screen positions for wraparound
      { x: e2Bounds.x - this.gameWidth, y: e2Bounds.y },
      { x: e2Bounds.x + this.gameWidth, y: e2Bounds.y },
      { x: e2Bounds.x, y: e2Bounds.y - this.gameHeight },
      { x: e2Bounds.x, y: e2Bounds.y + this.gameHeight },
      { x: e2Bounds.x - this.gameWidth, y: e2Bounds.y - this.gameHeight },
      { x: e2Bounds.x + this.gameWidth, y: e2Bounds.y - this.gameHeight },
      { x: e2Bounds.x - this.gameWidth, y: e2Bounds.y + this.gameHeight },
      { x: e2Bounds.x + this.gameWidth, y: e2Bounds.y + this.gameHeight }
    ];

    for (const pos of positions) {
      const virtualBounds = {
        x: pos.x,
        y: pos.y,
        width: e2Bounds.width,
        height: e2Bounds.height
      };

      if (this.rectanglesCollide(e1Bounds, virtualBounds)) {
        return true;
      }
    }

    return false;
  }

  private rectanglesCollide(rect1: any, rect2: any): boolean {
    return rect1.x < rect2.x + rect2.width &&
           rect1.x + rect1.width > rect2.x &&
           rect1.y < rect2.y + rect2.height &&
           rect1.y + rect1.height > rect2.y;
  }

  public calculateDistance(entity1: GameObject, entity2: GameObject): number {
    const dx = entity1.position.x - entity2.position.x;
    const dy = entity1.position.y - entity2.position.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  public calculateWraparoundDistance(entity1: GameObject, entity2: GameObject): number {
    // Calculate minimum distance accounting for wraparound
    let minDistance = this.calculateDistance(entity1, entity2);

    // Check distances to wraparound positions
    const positions = [
      { x: entity2.position.x - this.gameWidth, y: entity2.position.y },
      { x: entity2.position.x + this.gameWidth, y: entity2.position.y },
      { x: entity2.position.x, y: entity2.position.y - this.gameHeight },
      { x: entity2.position.x, y: entity2.position.y + this.gameHeight },
      { x: entity2.position.x - this.gameWidth, y: entity2.position.y - this.gameHeight },
      { x: entity2.position.x + this.gameWidth, y: entity2.position.y - this.gameHeight },
      { x: entity2.position.x - this.gameWidth, y: entity2.position.y + this.gameHeight },
      { x: entity2.position.x + this.gameWidth, y: entity2.position.y + this.gameHeight }
    ];

    for (const pos of positions) {
      const dx = entity1.position.x - pos.x;
      const dy = entity1.position.y - pos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < minDistance) {
        minDistance = distance;
      }
    }

    return minDistance;
  }
}